import { Button } from "@astryxdesign/core/Button";
import { Center } from "@astryxdesign/core/Center";
import { FormLayout } from "@astryxdesign/core/FormLayout";
import { Heading } from "@astryxdesign/core/Heading";
import { Text } from "@astryxdesign/core/Text";
import { TextInput } from "@astryxdesign/core/TextInput";
import { HStack, VStack } from "@astryxdesign/core/Stack";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { submitOnEnter } from "../lib/forms";
import { api, ApiError } from "../lib/api";
import { HqMark, KeyIcon } from "../lib/icons";
import { hasPasskey, loginWithPasskey } from "../lib/passkey";

export function LoginPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [passkeyReady, setPasskeyReady] = useState(false);

  useEffect(() => {
    void hasPasskey().then(setPasskeyReady);
  }, []);

  async function enter(): Promise<void> {
    qc.clear();
    await navigate({ to: "/", search: { c: "all", s: "open" } });
  }

  async function withPasskey() {
    setError(null);
    try {
      await loginWithPasskey();
      await enter();
    } catch {
      setError("Passkey sign-in failed.");
    }
  }

  async function commit() {
    if (!password || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.login(password);
      await enter();
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 429
          ? "Too many attempts — wait a bit."
          : "Wrong password.",
      );
      setBusy(false);
    }
  }

  return (
    <Center height="100dvh" axis="both">
      <VStack gap={6} width={320} paddingInline={4}>
        <VStack gap={1} hAlign="center">
          <HStack gap={1.5} align="center">
            <HqMark width="1.8em" height="1.8em" />
            <Heading level={1}>hq</Heading>
          </HStack>
          <Text type="supporting">the private ideas board</Text>
        </VStack>
        <form
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            void commit();
          }}
          onKeyDown={submitOnEnter(() => void commit())}
        >
          <FormLayout>
            <TextInput
              type="password"
              label="Password"
              isLabelHidden
              placeholder="Password"
              size="lg"
              value={password}
              onChange={setPassword}
              hasAutoFocus
              {...(error ? { status: { type: "error" as const, message: error } } : {})}
            />
            <Button type="submit" variant="primary" size="lg" width="100%" label="Enter" isLoading={busy} />
          </FormLayout>
        </form>
        {passkeyReady ? (
          <Button
            label="Sign in with passkey"
            variant="secondary"
            size="lg"
            width="100%"
            icon={<KeyIcon />}
            clickAction={withPasskey}
          />
        ) : null}
      </VStack>
    </Center>
  );
}
